import {
  combine,
  createDomain,
  createEffect,
  createEvent,
  sample,
} from "effector";
import { PAGINATION_LIMIT } from "../../../shared/configs/consts";
import { createGate } from "effector-react";
import { startOfDay, endOfDay, format, parseISO } from "date-fns";
import { CampaignListFilters, GetCampaignsResponse } from "../types";
import { campaignsApi } from "../api";

const domain = createDomain();

const Gate = createGate();

const updateFilter = createEvent<Partial<CampaignListFilters>>();
const applyFilters = createEvent();
const resetFilters = createEvent();
const $filters = domain.createStore<CampaignListFilters>({});

sample({
  clock: resetFilters,
  fn: () => ({}),
  target: $filters,
});

sample({
  source: $filters,
  clock: updateFilter,
  fn: (filters, payload) => ({ ...filters, ...payload }),
  target: $filters,
});

const $page = domain.createStore(1);
const setPage = createEvent<number>();

sample({
  clock: setPage,
  target: $page,
});

const getList = createEvent();
const fetchListFx = createEffect(campaignsApi.getList);
const $data = domain.createStore<GetCampaignsResponse | null>(null);

const $isFetched = domain
  .createStore(false)
  .on(fetchListFx.finally, () => true);

const $isLoading = combine(
  {
    isFetched: $isFetched,
    pending: fetchListFx.pending,
  },
  ({ isFetched, pending }) => !isFetched && pending
);

const $list = $data.map((data) =>
  (data?.data ?? []).map((row) => ({
    ...row,
    createdAt: format(parseISO(row.createdAt), "dd/MM/yyyy HH:mm"),
    updatedAt: format(parseISO(row.updatedAt), "dd/MM/yyyy HH:mm"),
  }))
);
const $totalPages = $data.map((data) =>
  Math.ceil((data?.meta?.total ?? 0) / PAGINATION_LIMIT)
);

sample({
  clock: [Gate.open, $page, applyFilters, resetFilters],
  filter: Gate.status,
  target: getList,
});

sample({
  source: {
    page: $page,
    filters: $filters,
  },
  clock: getList,
  fn: ({ page, filters }) => {
    return {
      ...filters,
      createdFrom: filters.createdFrom
        ? startOfDay(new Date(filters.createdFrom)).toISOString()
        : undefined,
      createdTo: filters.createdTo
        ? endOfDay(new Date(filters.createdTo)).toISOString()
        : undefined,
      limit: PAGINATION_LIMIT,
      offset: (page - 1) * PAGINATION_LIMIT,
    };
  },
  target: fetchListFx,
});

sample({
  clock: fetchListFx.doneData,
  target: $data,
});

export const campaignEntityModel = {
  $page,
  $totalPages,
  $list,
  $filters,
  applyFilters,
  resetFilters,
  setPage,
  updateFilter,
  getList,
  $isLoading,
  Gate,
};
